import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { debounce, uniqBy, upperCase } from 'lodash';
import useStore from 'store';
import useUserStore from 'store/user';
import { User } from 'types/schema-generated-types';
import { ReportEntityType, ReportProduct } from '../../../../types';
import { useField } from 'formik';
import { ENTITY_SEARCH_LIMIT, ENTITY_TYPE_OPTIONS_MAP } from '../../../../utils/report-param-utils';

type HookReturnValues = [
  { options: any[]; selectedItem: any; loading: boolean },
  {
    handleSearch: (e: React.ChangeEvent<{}>, value: any) => void;
    handleChangeEntity: (e: React.ChangeEvent<{}>, value: any) => void;
  }
];

const useSearchableOptions = ({
  name,
  entityType,
  productFamilyCode
}: {
  name: string;
  entityType: ReportEntityType;
  productFamilyCode: ReportProduct;
}): HookReturnValues => {
  const [field, , { setValue }] = useField(name);

  const {
    parseQueryData,
    query,
    getEntityQuery,
    queryVariables,
    requiredOrgPrivilege,
    requiredClassroomPrivilege
  } = ENTITY_TYPE_OPTIONS_MAP[entityType];
  const [{ userPrivilegesCache }] = useStore();
  const [{ me }] = useUserStore();
  const [search, { data: searchData, loading: searchLoading }] = useLazyQuery(query);

  const [
    getEntity,
    { data: entityData, loading: entityLoading, called: getEntityCalled }
  ] = useLazyQuery(getEntityQuery);
  const [showSpinner, setShowSpinner] = useState(false);
  const entityFilterTypes = [ReportEntityType.SCHOOL, ReportEntityType.DISTRICT];
  const loading = showSpinner || searchLoading;

  const getOptions = () => {
    let entityOptions: any[] = [];

    if (searchData && !loading) {
      entityOptions = [...parseQueryData(searchData, me as User, userPrivilegesCache)];
    }

    if (entityData?.entity && !entityLoading) {
      entityOptions.push(entityData?.entity);
    }
    const maybeFilterOptions = entityFilterTypes.includes(entityType)
      ? entityOptions.filter((x: any) => x.type === upperCase(entityType))
      : entityOptions;

    return uniqBy(maybeFilterOptions, 'id');
  };

  const options = getOptions();
  const entityId = field.value;
  const selectedItem = options.find((item: any) => item.id === entityId) || null;

  const productRequiredClassroomPrivilege = requiredClassroomPrivilege?.[productFamilyCode];
  const productRequiredOrgPrivilege = requiredOrgPrivilege?.[productFamilyCode];

  const debouncedSearch = React.useMemo(() => {
    return debounce((event: React.ChangeEvent<{}>, searchText: string) => {
      search({
        variables: {
          ...queryVariables,
          ...(!!productRequiredClassroomPrivilege && {
            classroomIds: userPrivilegesCache.getPrivilegedClassroomIds(
              productRequiredClassroomPrivilege
            )
          }),
          ...(!!productRequiredOrgPrivilege && {
            organizationIds: userPrivilegesCache.getPrivilegedOrgIds(productRequiredOrgPrivilege)
          }),
          searchText,
          limit: ENTITY_SEARCH_LIMIT
        }
      });
      setShowSpinner(false);
    }, 500);
  }, [
    productRequiredClassroomPrivilege,
    productRequiredOrgPrivilege,
    queryVariables,
    search,
    setShowSpinner,
    userPrivilegesCache
  ]);

  const handleSearch = React.useMemo(() => {
    return (event: React.ChangeEvent<{}>, searchText: string) => {
      setShowSpinner(true);
      debouncedSearch(event, searchText);
    };
  }, [debouncedSearch, setShowSpinner]);

  useEffect(() => {
    if (!entityId || selectedItem || entityLoading) return;

    if (getEntityCalled) {
      setValue(null);
    } else {
      getEntity({ variables: { id: entityId } });
    }
  }, [entityId, getEntity, entityLoading, setValue, selectedItem, getEntityCalled]);

  const handleChangeEntity = (e: any, value: any): void => {
    setValue(value?.id ?? null);
  };

  return [
    { options, selectedItem, loading },
    { handleSearch, handleChangeEntity }
  ];
};

export default useSearchableOptions;
